import { useEffect, useRef, useState } from 'react';
import type { ColumnDef } from '../lib/columns.js';

interface Props {
  columns: ColumnDef[];
  visible: Set<string>;
  onChange: (next: Set<string>) => void;
}

const POPOVER_W = 260;

/**
 * Кнопка «Колонки» + поповер с чекбоксами. Поповер позиционируется fixed
 * с прижатием к краям экрана — не обрезается, где бы кнопка ни оказалась.
 */
export function ColumnsToggle({ columns, visible, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  function openPopover() {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const top = rect.bottom + 6;
    const left = Math.max(8, Math.min(rect.right - POPOVER_W, window.innerWidth - POPOVER_W - 8));
    setPos({ top, left, maxHeight: Math.max(160, window.innerHeight - top - 16) });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Скролл страницы/таблицы прячет поповер (он fixed и «отклеился» бы от
    // кнопки), но прокрутка ВНУТРИ самого поповера закрывать его не должна.
    const onScrollResize = (e?: Event) => {
      if (e && e.target instanceof Node && wrapRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScrollResize);
    window.addEventListener('scroll', onScrollResize, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScrollResize);
      window.removeEventListener('scroll', onScrollResize, true);
    };
  }, [open]);

  function toggle(key: string) {
    const next = new Set(visible);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  }

  return (
    <div className="columns-wrap" ref={wrapRef}>
      <button ref={btnRef} className="btn-secondary" onClick={() => (open ? setOpen(false) : openPopover())} title="Настроить колонки">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ verticalAlign: -2, marginRight: 6 }}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M9 3v18M15 3v18" />
        </svg>
        Колонки ({visible.size})
      </button>
      {open && pos && (
        <div
          className="columns-popover"
          style={{ position: 'fixed', top: pos.top, left: pos.left, right: 'auto', width: POPOVER_W, maxHeight: pos.maxHeight }}
        >
          {columns.map((c) => (
            <button key={c.key} className="col-item" onClick={() => toggle(c.key)}>
              <input type="checkbox" checked={visible.has(c.key)} readOnly tabIndex={-1} />
              <span>{c.label}</span>
            </button>
          ))}
          <div className="col-actions">
            <button className="btn-ghost" style={{ fontSize: 12, padding: '5px 8px' }}
              onClick={() => onChange(new Set(columns.map((c) => c.key)))}>
              Показать все
            </button>
            <button className="btn-ghost" style={{ fontSize: 12, padding: '5px 8px' }}
              onClick={() => onChange(new Set(columns.filter((c) => c.defaultOn).map((c) => c.key)))}>
              Сбросить
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
